import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "../../style/scss/tree.scss";
import { MdAddBox } from "react-icons/md";
import { FaRegSquare, FaMinusSquare } from "react-icons/fa";

function TreeViewNode(props) {
  const nodeId = props.value.id ?? props.value.ID;
  const [open, setOpen] = useState((props.openIDs || []).indexOf(nodeId) >= 0);
  const hasChildren = (((props || {}).value || {}).children || []).length > 0;

  const toggleOpen = (e) => {
    e?.stopPropagation();
    if (props.value.children && props.value.children.length > 0) {
      props.toggleNodeOpen(props.value);
      setOpen(!open);
    }
  };

  const onClick = (e) => {
    e?.stopPropagation();
    props.onChange(props.value, e);
  };

  return (
    <div className={"TreeNode" + (nodeId === props.selectedID ? " selected" : "")}>
      <span className="NodeTitle">
        <span className="nodeIcons" onClick={toggleOpen}>
          {hasChildren ? (
            (props.openIDs || []).indexOf(nodeId) >= 0 ? (
              <FaMinusSquare />
            ) : (
              <MdAddBox />
            )
          ) : (
            <FaRegSquare />
          )}
        </span>
        <span className="nodeText" onClick={onClick}>
          {props.value.Name}
        </span>
      </span>
      {hasChildren ? (
        <div
          className={
            "nodeChildren " +
            ((props.openIDs || []).indexOf(nodeId) >= 0 ? "open" : "closed")
          }
        >
          {props.value.children.map((e) => {
            const childId = e.id ?? e.ID;
            return (
              <TreeViewNode
                key={childId}
                value={e}
                openIDs={props.openIDs}
                toggleNodeOpen={props.toggleNodeOpen}
                onChange={props.onChange}
                selectedID={props.selectedID}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function TreeView(props) {
  const initial =
    props.formObject?.object?.[props.field] ??
    props.formObject?.object?.[props.fieldDisplay] ??
    0;
  const [selectedID, setSelected] = useState(initial);
  const [openIDs, setOpen] = useState([]);
  const [rootNodes, setRoots] = useState([]);
  const [treeOpen, setTreeOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const categoryId =
    props.formObject?.object?.[props.field] ??
    props.formObject?.object?.[
      props.field ? props.field.charAt(0).toLowerCase() + props.field.slice(1) : ""
    ] ??
    0;
  const displayName =
    props.formObject?.object?.[props.fieldDisplay] ||
    props.formObject?.object?.[
      props.fieldDisplay
        ? props.fieldDisplay.charAt(0).toLowerCase() + props.fieldDisplay.slice(1)
        : ""
    ] ||
    (selectedID || categoryId ? `Category #${selectedID || categoryId}` : "");

  // Keep selection in sync when parent form hydrates after mount.
  useEffect(() => {
    const nextId = Number(categoryId) || 0;
    if (nextId !== selectedID) setSelected(nextId);
  }, [categoryId]);

  const toggleTree = async (e) => {
    if (e) e.stopPropagation();
    const opening = !treeOpen;
    if (opening && rootNodes.length === 0 && props.getData) {
      setLoading(true);
      try {
        const res = await props.getData();
        const list = (res || []).map((n) => ({
          id: n.ID ?? n.id,
          ID: n.ID ?? n.id,
          Parent: n.Parent ?? n.parent ?? 0,
          Name: n.Name ?? n.name ?? "",
          children: [],
        }));
        list.sort((a, b) => ((a.Name || "") > (b.Name || "") ? 1 : -1));
        const data = treeifyData(list);
        if (selectedID) {
          let element = data.find((x) => x.id === selectedID);
          while (element) {
            openNode(element);
            element = element.ParentNode;
          }
        }
        setRoots(data.filter((x) => !x.Parent));
      } finally {
        setLoading(false);
      }
    }
    setTreeOpen(opening);
  };

  const onChange = (node) => {
    const id = node.id ?? node.ID;
    setSelected(id);
    if (props.formObject?.onChange) {
      props.formObject.onChange(id, props.field, "CategorySelect");
      if (props.fieldDisplay) {
        props.formObject.onChange(node.Name, props.fieldDisplay, "CategorySelect");
      }
    }
    setTreeOpen(false);
  };

  const toggleNodeOpen = (node) => {
    const id = node.id ?? node.ID;
    const next = [...openIDs];
    const index = next.indexOf(id);
    if (index >= 0) next.splice(index, 1);
    else next.push(id);
    setOpen(next);
  };

  const openNode = (node) => {
    const id = node.id ?? node.ID;
    if (node.children?.length && openIDs.indexOf(id) === -1) {
      setOpen((prev) => [...prev, id]);
    }
  };

  const treeifyData = (data) => {
    data.forEach((cat) => {
      if (cat.Parent > 0) {
        const parent = data.find((f) => f.id === cat.Parent);
        if (parent) {
          cat.ParentNode = parent;
          if (!parent.children) parent.children = [];
          parent.children.push(cat);
        }
      }
    });
    return data;
  };

  return (
    <div className="field TreeView form-group">
      {props.label ? (
        <label className="form-label" htmlFor={`treeview-${props.field}`}>
          {props.label}
        </label>
      ) : null}
      <button
        type="button"
        id={`treeview-${props.field}`}
        className="form-input treeview-trigger"
        data-testid="treeview-category-trigger"
        onClick={toggleTree}
      >
        {loading
          ? "Loading categories…"
          : displayName || "Select category…"}
      </button>
      {treeOpen
        ? createPortal(
            <div
              className="treeBackdrop"
              data-testid="treeview-backdrop"
              onClick={(e) => {
                if (e.target.classList.contains("treeBackdrop")) setTreeOpen(false);
              }}
            >
              <div className="Tree" role="dialog" aria-label={props.label || "Category"}>
                {rootNodes.length === 0 ? (
                  <p className="byline">No categories found.</p>
                ) : (
                  rootNodes.map((e) => (
                    <TreeViewNode
                      key={e.id}
                      value={e}
                      openIDs={openIDs}
                      toggleNodeOpen={toggleNodeOpen}
                      onChange={onChange}
                      selectedID={selectedID}
                    />
                  ))
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
